import logger from '../utils/logger';
// Client-side rate limiter for API requests
class RateLimiter {
  constructor() {
    this.requests = new Map(); // Map of endpoint -> timestamps array

    // Get rate limits from environment variables or use defaults
    // Environment variables take precedence over hardcoded values
    const getEnvNumber = (key, defaultValue) => {
      const value = process.env[key];
      return value ? parseInt(value, 10) : defaultValue;
    };

    this.config = {
      // Default limits: configurable via REACT_APP_RATE_LIMIT_DEFAULT
      // Set to 999 (effectively unlimited) for normal operations
      maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_DEFAULT', 999),
      windowMs: 60000, // 1 minute

      // Specific endpoint limits (configurable via environment)
      endpoints: {
        '/auth/login': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_LOGIN', 10),
          windowMs: 60000 // 10 requests per minute
        },
        '/auth/register': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_REGISTER', 10),
          windowMs: 60000 // 10 requests per minute (changed from 5 minutes)
        },
        '/auth/refresh': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_REFRESH', 30),
          windowMs: 60000 // 30 requests per minute
        },
        '/chat': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_CHAT', 30),
          windowMs: 60000 // 30 requests per minute
        },
        '/admin': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_ADMIN', 50),
          windowMs: 60000 // 50 requests per minute
        },
        '/workspace-documents/upload': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_UPLOAD', 20),
          windowMs: 60000 // 20 requests per minute
        },
        '/profile': {
          maxRequests: getEnvNumber('REACT_APP_RATE_LIMIT_PROFILE', 30),
          windowMs: 60000 // 30 requests per minute
        },
        // No rate limiting for these operations
        '/gantt/data': { maxRequests: 999, windowMs: 60000 },
        '/gantt/summary': { maxRequests: 999, windowMs: 60000 },
        '/gantt/export': { maxRequests: 999, windowMs: 60000 },
      }
    };

    // Log configuration on initialization (only in development)
    if (process.env.NODE_ENV === 'development') {
      logger.debug('UTILS', 'Rate Limiter Configuration:', {
        default: this.config.maxRequests,
        login: this.config.endpoints['/auth/login'].maxRequests,
        chat: this.config.endpoints['/chat'].maxRequests,
        admin: this.config.endpoints['/admin'].maxRequests,
      });
    }
  }

  // Check if request is allowed
  isAllowed(endpoint, method = 'GET') {
    // Option to disable rate limiting in development
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DISABLE_RATE_LIMIT === 'true') {
      return {
        allowed: true,
        remaining: 999,
        isDevelopment: true
      };
    }

    // EXEMPT: DELETE and PUT/PATCH operations (no rate limiting on updates/deletes)
    const methodUpper = method.toUpperCase();
    if (methodUpper === 'DELETE' || methodUpper === 'PUT' || methodUpper === 'PATCH') {
      return {
        allowed: true,
        remaining: 999,
        isUpdateOrDelete: true
      };
    }

    // EXEMPT: Table search endpoints (no rate limiting on searches)
    const tableSearchEndpoints = [
      '/search',
      '/workspace-faqs',
      '/bookings',
      '/users',
      '/analytics',
      '/logs',
      '/admin',
    ];

    // Check if this is a table search request
    if (tableSearchEndpoints.some(pe => endpoint.includes(pe))) {
      return {
        allowed: true,
        remaining: 999,
        isTableSearch: true
      };
    }

    // EXEMPT: Polling endpoints to avoid false positives
    const pollingEndpoints = [
      '/workspace-documents',
      '/documents',
      '/status',
      '/progress',
      '/health',
      '/refresh', // Page refresh
      '/auth/profile', // PendingApproval polling
    ];

    // Check if this is a polling request (GET request to document endpoints)
    if (method.toUpperCase() === 'GET' && pollingEndpoints.some(pe => endpoint.includes(pe))) {
      return {
        allowed: true,
        remaining: 999,
        isPolling: true
      };
    }
    
    const key = `${method.toUpperCase()}:${endpoint}`;
    const now = Date.now();
    
    // Get configuration for this endpoint
    const endpointConfig = this.getEndpointConfig(endpoint);
    const { maxRequests, windowMs } = endpointConfig;
    
    // Get existing requests for this endpoint
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const timestamps = this.requests.get(key);
    
    // Remove timestamps outside the window
    const validTimestamps = timestamps.filter(timestamp => now - timestamp < windowMs);
    
    // Update the stored timestamps
    this.requests.set(key, validTimestamps);
    
    // Check if we're under the limit
    if (validTimestamps.length >= maxRequests) {
      const oldestRequest = Math.min(...validTimestamps);
      const timeUntilReset = windowMs - (now - oldestRequest);
      
      return {
        allowed: false,
        retryAfter: Math.ceil(timeUntilReset / 1000), // seconds
        message: `Rate limit exceeded. Try again in ${Math.ceil(timeUntilReset / 1000)} seconds.`
      };
    }
    
    // Record this request
    validTimestamps.push(now);
    this.requests.set(key, validTimestamps);
    
    return {
      allowed: true,
      remaining: maxRequests - validTimestamps.length
    };
  }

  // Get configuration for specific endpoint
  getEndpointConfig(endpoint) {
    // Check for exact match first
    if (this.config.endpoints[endpoint]) {
      return this.config.endpoints[endpoint];
    }
    
    // Check for partial matches
    for (const [configEndpoint, config] of Object.entries(this.config.endpoints)) {
      if (endpoint.includes(configEndpoint)) {
        return config;
      }
    }
    
    // Return default config
    return {
      maxRequests: this.config.maxRequests,
      windowMs: this.config.windowMs
    };
  }

  // Get current status for endpoint
  getStatus(endpoint, method = 'GET') {
    const key = `${method.toUpperCase()}:${endpoint}`;
    const now = Date.now();
    
    if (!this.requests.has(key)) {
      return { requests: 0, limit: this.getEndpointConfig(endpoint).maxRequests };
    }
    
    const timestamps = this.requests.get(key);
    const endpointConfig = this.getEndpointConfig(endpoint);
    const validTimestamps = timestamps.filter(timestamp => now - timestamp < endpointConfig.windowMs);
    
    return {
      requests: validTimestamps.length,
      limit: endpointConfig.maxRequests,
      resetTime: validTimestamps.length > 0 ? Math.min(...validTimestamps) + endpointConfig.windowMs : null
    };
  }

  // Clear all stored requests (for testing or reset)
  reset() {
    this.requests.clear();
  }

  // Update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create singleton instance
const rateLimiter = new RateLimiter();

export default rateLimiter;